import {
  parseCurrentAdminRbacPermissionsResponse,
  type CurrentAdminRbacPermissionsResponse,
} from '@gozoki/api-types'

/**
 * Ce que le BO lit du contrat `GET /rbac/me/permissions` : ses grants v2. Les champs hérités que le
 * backend sert encore (grants v1 et constantes de l'ancien interrupteur) sont tolérés par le
 * parseur, mais aucun écran ne les lit.
 */
export type RbacContract = Pick<
  CurrentAdminRbacPermissionsResponse,
  'schemaVersion' | 'authorizationModel' | 'grantsV2'
>

/** Contrat exploitable, ou `null` : réponse absente, en erreur ou mal formée. */
export const parseRbacContract = (value: unknown): RbacContract | null =>
  parseCurrentAdminRbacPermissionsResponse(value)

/** Réponse exploitable ⇒ le BO peut lui faire confiance pour installer l'ability. */
export const isUsableRbacResponse = (value: unknown): boolean => parseRbacContract(value) !== null

/**
 * - `signed-out` : aucun compte.
 * - `loading` : première réponse attendue, rien à afficher.
 * - `ready` : contrat exploitable frais ; ses grants v2 décident seuls.
 * - `stale` : réponse courante inexploitable, mais une réponse exploitable de la session sert
 *   encore de référence : l'ability reste celle de ce contrat.
 * - `unavailable` : aucun contrat exploitable de toute la session. Sans contrat, aucun droit :
 *   écran bloquant et nouvel essai automatique.
 */
export type RbacSessionState = 'signed-out' | 'loading' | 'ready' | 'stale' | 'unavailable'

export interface RbacSessionResolution {
  state: RbacSessionState
  /** Contrat retenu pour l'ability ; `null` ⇒ aucune permission. */
  contract: RbacContract | null
  /** Dernière réponse en échec : bandeau (`stale`) ou écran bloquant (`unavailable`). */
  degraded: boolean
}

export interface RbacSessionInput {
  hasUser: boolean
  pending: boolean
  error: boolean
  /** Contrat exploitable de la réponse courante, `null` si absente/en erreur/incompatible. */
  contract: RbacContract | null
  /** Dernier contrat exploitable obtenu pendant la session. */
  remembered: RbacContract | null
}

/**
 * Seul un contrat exploitable ouvre l'interface. Une réponse absente, en erreur ou de forme
 * incompatible (502 passager, BO déployé avant le backend) garde le dernier contrat exploitable de
 * la session s'il y en a un ; sinon la session est `unavailable` : le BO bloque au lieu d'ouvrir
 * l'interface en grand, et la requête retente d'elle-même.
 */
export const resolveRbacSession = ({
  hasUser,
  pending,
  error,
  contract,
  remembered,
}: RbacSessionInput): RbacSessionResolution => {
  if (!hasUser) return { state: 'signed-out', contract: null, degraded: false }
  if (contract !== null) return { state: 'ready', contract, degraded: false }

  // On garde l'ability déjà installée plutôt que de tout refermer sur un échec passager.
  if (remembered !== null) return { state: 'stale', contract: remembered, degraded: !pending }

  if (pending && !error) return { state: 'loading', contract: null, degraded: false }

  return { state: 'unavailable', contract: null, degraded: true }
}
